import React, { useEffect, useState } from 'react';
import { Alert, Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles, DISPLAY_FONT } from '../v10/theme';
import { GlassFill, GoldButton } from '../v10/ui';

export interface CrossAlertButton {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

interface CrossAlertState {
  title: string;
  message?: string;
  buttons: CrossAlertButton[];
}

// react-native-web's Alert.alert() is a documented no-op (it has no web
// implementation), so on web every menu/confirm built on Alert.alert
// silently does nothing. This renders an equivalent Modal instead; on
// native it just delegates straight to the real Alert.
let listener: ((state: CrossAlertState | null) => void) | null = null;

export function crossAlert(title: string, message?: string, buttons?: CrossAlertButton[]): void {
  if (Platform.OS !== 'web') {
    Alert.alert(title, message, buttons as any);
    return;
  }
  const resolvedButtons = buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }];
  listener?.({ title, message, buttons: resolvedButtons });
}

/** Mount once near the root of the web app; renders whatever crossAlert() last requested. */
export function CrossAlertHost() {
  const [state, setState] = useState<CrossAlertState | null>(null);
  const styles = useStyles();

  useEffect(() => {
    listener = setState;
    return () => {
      listener = null;
    };
  }, []);

  if (Platform.OS !== 'web' || !state) return null;

  const dismiss = (button: CrossAlertButton) => {
    setState(null);
    button.onPress?.();
  };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={() => setState(null)}>
      <Pressable style={styles.backdrop} onPress={() => setState(null)}>
        <Pressable style={styles.card} onPress={() => {}}>
          <GlassFill tone="strong" />
          <Text style={styles.title}>{state.title}</Text>
          {state.message ? <Text style={styles.message}>{state.message}</Text> : null}
          <View style={styles.buttonList}>
            {state.buttons.map((button, idx) =>
              button.style === 'destructive' ? (
                <Pressable
                  key={idx}
                  style={({ pressed }) => [styles.destructiveBtn, pressed && { opacity: 0.8 }]}
                  onPress={() => dismiss(button)}
                >
                  <Text style={styles.destructiveText}>{button.text}</Text>
                </Pressable>
              ) : (
                <GoldButton
                  key={idx}
                  label={button.text}
                  ghost={button.style === 'cancel'}
                  onPress={() => dismiss(button)}
                />
              ),
            )}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)', // pure black scrim, same in both modes
      alignItems: 'center',
      justifyContent: 'center',
    },
    card: {
      width: 320,
      maxWidth: '90%',
      borderRadius: 22,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: t.navyLine,
      paddingVertical: 20,
      paddingHorizontal: 20,
      // @ts-ignore web-only
      boxShadow: `0 10px 30px ${t.glowSoft}`,
    },
    title: { fontSize: 16, color: t.ivory, marginBottom: 6, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
    message: { fontSize: 13.5, lineHeight: 19, color: t.ivory, marginBottom: 8 },
    buttonList: { marginTop: 10, gap: 8 },
    destructiveBtn: {
      paddingVertical: 12,
      borderRadius: 999,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: t.lapsed,
      backgroundColor: 'rgba(226,75,74,0.12)',
    },
    destructiveText: { fontSize: 14, fontWeight: '700', color: t.lapsed },
  }),
);
